import { Action, Reducer } from 'redux';
import { GlobalState, AppThunkAction } from '.';
import { Folder } from '../models';
import { ActionDefinition, createDate, createMessage, DefaultActionDefinition } from '../models';
import { produce } from 'immer';
import moment from 'moment';
import * as MessageStore from './Message';
import { getFolders, createFolder, moveFolder, deleteFolder, renameFolder } from '../services/Api.Folder.Service';
import { getAccessToken } from '../auth/service';
import { getChildrenRecursive, getPathsRecursive } from 'utils';
import { getParentFoldersForFolder } from '../services/util';
import _ from 'lodash';

export enum ActionTypes {
    LoadFoldersStart = '@@folders/LOAD_FOLDERS/START',
    LoadFoldersComplete = '@@folders/LOAD_FOLDERS/COMPLETE',
    LoadFoldersFailed = '@@folders/LOAD_FOLDERS/FAILED',
    CreateFolderStart = '@@folders/CREATE/START',
    CreateFolderComplete = '@@folders/CREATE/COMPLETE',
    CreateFolderFailed = '@@folders/CREATE/FAILED',
    MoveFolderStart = '@@folders/MOVE/START',
    MoveFolderComplete = '@@folders/MOVE/COMPLETE',
    MoveFolderFailed = '@@folders/MOVE/FAILED',
    DeleteFolderStart = '@@folders/DELETE/START',
    DeleteFolderComplete = '@@folders/DELETE/COMPLETE',
    DeleteFolderFailed = '@@folders/DELETE/FAILED',
    RenameFolderStart = '@@folders/RENAME/START',
    RenameFolderComplete = '@@folders/RENAME/COMPLETE',
    RenameFolderFailed = '@@folders/RENAME/FAILED',
}

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface FolderState {
    loadFolders: ActionDefinition;
    createFolder: ActionDefinition;
    renameFolder: ActionDefinition;
    moveFolder: ActionDefinition;
    deleteFolder: ActionDefinition;
    folders: Folder[]
}

export const initialState: FolderState = {
    loadFolders: DefaultActionDefinition,
    createFolder: DefaultActionDefinition,
    renameFolder: DefaultActionDefinition,
    moveFolder: DefaultActionDefinition,
    deleteFolder: DefaultActionDefinition,
    folders: []
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
// Use @typeName and isActionType for type detection that works even after serialization/deserialization.

export interface LoadFoldersStart { type: ActionTypes.LoadFoldersStart }
export interface LoadFoldersComplete { type: ActionTypes.LoadFoldersComplete, folders: Folder[] }
export interface LoadFoldersFailed { type: ActionTypes.LoadFoldersFailed, error: Error | null }
export interface CreateFolderStart { type: ActionTypes.CreateFolderStart }
export interface CreateFolderComplete { type: ActionTypes.CreateFolderComplete, folders: Folder[] }
export interface CreateFolderFailed { type: ActionTypes.CreateFolderFailed, error: Error | null }
export interface MoveFolderStart { type: ActionTypes.MoveFolderStart }
export interface MoveFolderComplete { type: ActionTypes.MoveFolderComplete, folders: Folder[] }
export interface MoveFolderFailed { type: ActionTypes.MoveFolderFailed, error: Error | null }
export interface DeleteFolderStart { type: ActionTypes.DeleteFolderStart }
export interface DeleteFolderComplete { type: ActionTypes.DeleteFolderComplete, folders: Folder[] }
export interface DeleteFolderFailed { type: ActionTypes.DeleteFolderFailed, error: Error | null }
export interface RenameFolderStart { type: ActionTypes.RenameFolderStart }
export interface RenameFolderComplete { type: ActionTypes.RenameFolderComplete, folders: Folder[] }
export interface RenameFolderFailed { type: ActionTypes.RenameFolderFailed, error: Error | null }

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
export type Actions =
    | LoadFoldersStart
    | LoadFoldersComplete
    | LoadFoldersFailed
    | CreateFolderStart
    | CreateFolderComplete
    | CreateFolderFailed
    | MoveFolderStart
    | MoveFolderComplete
    | MoveFolderFailed
    | DeleteFolderStart
    | DeleteFolderComplete
    | DeleteFolderFailed
    | RenameFolderStart
    | RenameFolderComplete
    | RenameFolderFailed;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const actionCreators = {
    loadFolders: (): AppThunkAction<Actions | MessageStore.Actions> => async (dispatch, getState) => {
        dispatch({ type: ActionTypes.LoadFoldersStart });
        try {
            const accessToken = await getAccessToken();
            const folders = await getFolders(accessToken);
            dispatch({ type: ActionTypes.LoadFoldersComplete, folders });
        } catch (error) {
            const err = error as any;
            dispatch({ type: ActionTypes.LoadFoldersFailed, error: err.message });
            dispatch(MessageStore.actionCreators.show(createMessage('Error loading folders', err.message, err)));
        }
    },
    createFolder: (name: string, parentFolderId?: string, onComplete?: (success: boolean, errors?: string[]) => void): AppThunkAction<Actions | MessageStore.Actions> => async (dispatch, getState) => {
        dispatch({ type: ActionTypes.CreateFolderStart });
        try {
            const accessToken = await getAccessToken();
            const folders = await createFolder(accessToken, name, parentFolderId);
            dispatch({ type: ActionTypes.CreateFolderComplete, folders });
            if (onComplete) {
                onComplete(true);
            }
        } catch (error) {
            const err = error as any;
            dispatch({ type: ActionTypes.CreateFolderFailed, error: err.message });
            //dispatch(MessageStore.actionCreators.show(createMessage('Error Creating Folder', err.message, err)));
            if (onComplete) {
                onComplete(false, [err.message]);
            }
        }
    },
    renameFolder: (name: string, folderId: string, onComplete?: (success: boolean, errors?: string[]) => void): AppThunkAction<Actions | MessageStore.Actions> => async (dispatch, getState) => {
        dispatch({ type: ActionTypes.RenameFolderStart });
        try {
            const accessToken = await getAccessToken();
            const folders = await renameFolder(accessToken, name, folderId);
            dispatch({ type: ActionTypes.RenameFolderComplete, folders });
            if (onComplete) {
                onComplete(true);
            }
        } catch (error) {
            const err = error as any;
            dispatch({ type: ActionTypes.RenameFolderFailed, error: err.message });
            //dispatch(MessageStore.actionCreators.show(createMessage('Error Creating Folder', err.message, err)));
            if (onComplete) {
                onComplete(false, [err.message]);
            }
        }
    },
    moveFolder: (folderId: string, newFolderId?: string, onComplete?: (success: boolean, errors?: string[]) => void): AppThunkAction<Actions | MessageStore.Actions> => async (dispatch, getState) => {
        dispatch({ type: ActionTypes.MoveFolderStart });
        try {
            const accessToken = await getAccessToken();
            const folders = await moveFolder(accessToken, folderId, newFolderId);
            dispatch({ type: ActionTypes.MoveFolderComplete, folders });
            if (onComplete) {
                onComplete(true);
            }
        } catch (error) {
            const err = error as any;
            dispatch({ type: ActionTypes.MoveFolderFailed, error: err.message });
            //dispatch(MessageStore.actionCreators.show(createMessage('Error Moving Folder', err.message, err)));
            if (onComplete) {
                onComplete(false, [err.message]);
            }
        }
    },
    deleteFolder: (folderId: string, onComplete?: (success: boolean, errors?: string[]) => void): AppThunkAction<Actions | MessageStore.Actions> => async (dispatch, getState) => {
        dispatch({ type: ActionTypes.DeleteFolderStart });
        try {
            const accessToken = await getAccessToken();
            const folders = await deleteFolder(accessToken, folderId);
            dispatch({ type: ActionTypes.DeleteFolderComplete, folders });
            if (onComplete) {
                onComplete(true);
            }
        } catch (error) {
            const err = error as any;
            dispatch({ type: ActionTypes.DeleteFolderFailed, error: err.message });
            //dispatch(MessageStore.actionCreators.show(createMessage('Error Deleting Folder', err.message, err)));
            if (onComplete) {
                onComplete(false, [err.message]);
            }
        }
    }
};

// ----------------
// UTILS - These are functions that aid in manipulating data from the current store

export const utils = {
    getPath: (folders: Folder[], folderId: string) => getPathsRecursive(folders, [], f => f.name, f => f.childFolders).find(p => p.item.id === folderId),
    getPathIds: (folders: Folder[], folderId: string) => getPathsRecursive(folders, [], f => f.id, f => f.childFolders).find(p => p.item.id === folderId),
    getParentFolders: (allFolders: Folder[], folder: Folder) => getParentFoldersForFolder(allFolders, folder)
}

// ----------------
// SELECTORS - These are functions that, given the application state, will access a particular piece of information
// from the current store

export const selectors = {
    folders: (state: GlobalState) => state.folder.folders,
    foldersFlattened: (state: GlobalState) => getChildrenRecursive(state.folder.folders, f => f.childFolders),
    loadFolders: (state: GlobalState) => state.folder.loadFolders,
    createFolder: (state: GlobalState) => state.folder.createFolder,
    renameFolder: (state: GlobalState) => state.folder.renameFolder,
    moveFolder: (state: GlobalState) => state.folder.moveFolder,
    deleteFolder: (state: GlobalState) => state.folder.deleteFolder,
    byId: (id: string) => (state: GlobalState) => getChildrenRecursive(state.folder.folders, f => f.childFolders).find(f => f.id.toLowerCase() === id.toLowerCase())
}

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

export const reducer: Reducer<FolderState> = (state: FolderState | undefined, incomingAction: Action): FolderState => {
    if (state === undefined) {
        return initialState;
    }

    const action = incomingAction as Actions;
    switch (action.type) {
        case ActionTypes.LoadFoldersStart: {
            const newState = produce(state, draft => {
                draft.loadFolders.is = true;
            });
            return newState;
        }
        case ActionTypes.LoadFoldersFailed: {
            const newState = produce(state, draft => {
                draft.loadFolders.is = false;
                draft.loadFolders.did = createDate(moment().utc());
                draft.loadFolders.error = action.error;
            });
            return newState;
        }
        case ActionTypes.LoadFoldersComplete: {
            const newState = produce(state, draft => {
                draft.loadFolders.is = false;
                draft.loadFolders.did = createDate(moment().utc());
                draft.folders = action.folders;
            });
            return newState;
        }
        case ActionTypes.CreateFolderStart: {
            const newState = produce(state, draft => {
                draft.createFolder.is = true;
            });
            return newState;
        }
        case ActionTypes.CreateFolderFailed: {
            const newState = produce(state, draft => {
                draft.createFolder.is = false;
                draft.createFolder.did = createDate(moment().utc());
                draft.createFolder.error = action.error;
            });
            return newState;
        }
        case ActionTypes.CreateFolderComplete: {
            const newState = produce(state, draft => {
                draft.createFolder.is = false;
                draft.createFolder.did = createDate(moment().utc());
                draft.folders = action.folders;
            });
            return newState;
        }
        case ActionTypes.RenameFolderStart: {
            const newState = produce(state, draft => {
                draft.renameFolder.is = true;
            });
            return newState;
        }
        case ActionTypes.RenameFolderFailed: {
            const newState = produce(state, draft => {
                draft.renameFolder.is = false;
                draft.renameFolder.did = createDate(moment().utc());
                draft.renameFolder.error = action.error;
            });
            return newState;
        }
        case ActionTypes.RenameFolderComplete: {
            const newState = produce(state, draft => {
                draft.renameFolder.is = false;
                draft.renameFolder.did = createDate(moment().utc());
                draft.folders = action.folders;
            });
            return newState;
        }
        case ActionTypes.MoveFolderStart: {
            const newState = produce(state, draft => {
                draft.moveFolder.is = true;
            });
            return newState;
        }
        case ActionTypes.MoveFolderFailed: {
            const newState = produce(state, draft => {
                draft.moveFolder.is = false;
                draft.moveFolder.did = createDate(moment().utc());
                draft.moveFolder.error = action.error;
            });
            return newState;
        }
        case ActionTypes.MoveFolderComplete: {
            const newState = produce(state, draft => {
                draft.moveFolder.is = false;
                draft.moveFolder.did = createDate(moment().utc());
                draft.folders = action.folders;
            });
            return newState;
        }
        case ActionTypes.DeleteFolderStart: {
            const newState = produce(state, draft => {
                draft.deleteFolder.is = true;
            });
            return newState;
        }
        case ActionTypes.DeleteFolderFailed: {
            const newState = produce(state, draft => {
                draft.deleteFolder.is = false;
                draft.deleteFolder.did = createDate(moment().utc());
                draft.deleteFolder.error = action.error;
            });
            return newState;
        }
        case ActionTypes.DeleteFolderComplete: {
            const newState = produce(state, draft => {
                draft.deleteFolder.is = false;
                draft.deleteFolder.did = createDate(moment().utc());
                draft.folders = action.folders;
            });
            return newState;
        }
        default: {
            return state;
        }
    }
};
